import { z } from '@harvestiq/zod';
import { zDecimalNullable, zDecimalRequired, zIsoDate } from '@harvestiq/zod';
import {
  CampaignCoverageTier,
  InsureIQPolicyTypes,
  RmaLrpTypeCodes,
  RmaLgmTypeCodes,
  LrpCampaignPdfColumns,
  LgmCampaignPdfColumns,
} from '@harvestiq/constants';
import { orgAppSettingsSchema } from '../org-app-settings/org-app-settings';
import { LrpQuote, lrpQuoteSchema } from '@harvestiq/usda-common';

// FIXME: This file contains multiple schemas and types related to quotes, campaigns, and responses that should be organized better.

export type InsureIQQuoteDownloadApiResponse = z.infer<
  typeof insureIQSendQuoteEmailResponseSchema
>;

// Quote campaign
export const insureIQTypeCodesSchema = z.union([
  z.nativeEnum(RmaLrpTypeCodes),
  z.nativeEnum(RmaLgmTypeCodes),
]);

// TODO: this need to be consolidated
export const rmaLgmQuoteSchema = z.object({
  marketingDate: z.string(),
  salesEffectiveDate: z.string(),
  typeCode: z.nativeEnum(RmaLgmTypeCodes),
  coveragePrice: z.number(),
  producerPremium: z.number(),
  basePremium: z.number(),
  deductible: z.number(),
  subsidyDollar: z.number(),
});

export type RmaLgmQuote = z.infer<typeof rmaLgmQuoteSchema>;

export const rmaQuoteSchema = z.union([lrpQuoteSchema, rmaLgmQuoteSchema]);
export type RmaQuote = z.infer<typeof rmaQuoteSchema>;

export function isRmaLrpQuote(quote: RmaQuote): quote is LrpQuote {
  return 'endDate' in quote;
}

export function isRmaLgmQuote(quote: RmaQuote): quote is RmaLgmQuote {
  return 'deductible' in quote;
}

export const rmaQuoteResponseSchema = z.record(
  z.string(),
  rmaQuoteSchema.array()
);

export const insureIQCampaignRecipientSchema = z.object({
  userId: z.string().uuid(),
  email: z.string().email(),
  name: z.string(),
  agentName: z.string().optional(),
  agentEmail: z.string().email().optional(),
});
export type InsureIQCampaignRecipient = z.infer<
  typeof insureIQCampaignRecipientSchema
>;

// TODO: split between LRP and LGM
export const insureIQQuoteResponseSchema = z.object({
  policyType: z.nativeEnum(InsureIQPolicyTypes),
  message: z.string().optional(),
  targetWeight: zDecimalNullable(),
  numberOfHead: z.number().int().min(1).nullable(),
  endorsementLength: z.coerce.number().nullable().optional(),
  coverageTier: z.nativeEnum(CampaignCoverageTier).nullable(),
  liveCattleTargetCwtPerHead: zDecimalRequired().nullable().optional(),
  feederCattleTargetCwtPerHead: zDecimalRequired().nullable().optional(),
  cornTargetBuPerHead: zDecimalRequired().nullable().optional(),
  filename: z.string(),
  fileUrl: z.string(),
  salesEffectiveDate: zIsoDate(),
  quotes: rmaQuoteResponseSchema,
  deductibles: z.array(z.number().int().min(0).max(150)).optional(),
  quoteColumns: z
    .array(
      z
        .nativeEnum(LrpCampaignPdfColumns)
        .or(z.nativeEnum(LgmCampaignPdfColumns))
    )
    .optional(),
});
export type InsureIQQuoteResponse = z.infer<typeof insureIQQuoteResponseSchema>;

export const insureIQInsuranceQuoteResponseSchema = z.object({
  data: insureIQQuoteResponseSchema.nullable(),
  orgAppSettings: orgAppSettingsSchema,
});
export type InsureIQInsuranceQuoteResponse = z.infer<
  typeof insureIQInsuranceQuoteResponseSchema
>;

export const insuranceQuoteTemplateDataSchema =
  insureIQQuoteResponseSchema.extend({
    unsubscribeURL: z.string().url().optional(),
  });

// TODO: Revisit and double-check which fields are really in use
export type InsuranceQuoteTemplateData = z.infer<
  typeof insuranceQuoteTemplateDataSchema
>;

export const insureIQSendQuoteEmailResponseSchema = z.object({
  data: z
    .union([
      z.object({
        data: z.array(z.any()).optional(),
      }),
      z.array(z.any()),
    ])
    .optional(),
  errors: z.array(z.any()).optional(),
});

export type InsureIQSendQuoteEmailResponse = z.infer<
  typeof insureIQSendQuoteEmailResponseSchema
>;
